/**
 * Thin analytics wrapper. Today this is a no-op + dev console.log + a push to
 * `window.dataLayer` (which a GTM container can consume later). The intent is
 * that *call sites* are stable now — switching to Mixpanel/PostHog/Segment is
 * a one-file change inside this wrapper.
 */

type TrackProps = Record<string, unknown>;

interface MaybeDataLayer {
  dataLayer?: { push: (entry: { event: string } & TrackProps) => void };
}

/**
 * Fire a product analytics event.
 *
 * @param event  Snake_case event name; keep it stable across releases.
 * @param props  Optional event properties. Avoid putting PII here.
 */
export function track(event: string, props: TrackProps = {}): void {
  try {
    if (typeof window === "undefined") return;

    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.log("[analytics]", event, props);
    }

    const w = window as unknown as MaybeDataLayer;
    if (w.dataLayer && typeof w.dataLayer.push === "function") {
      w.dataLayer.push({ event, ...props });
    }
  } catch {
    // Swallow — analytics must never break a user flow.
  }
}

/** Canonical event-name constants — keep here so call sites grep cleanly. */
export const AnalyticsEvents = {
  BrochureViewed: "brochure_viewed",
  StartTrialCtaClicked: "start_trial_cta_clicked",
  SignupFreeSubmitted: "signup_free_submitted",
  TrialStarted: "trial_started",
  FeatureLockedHit: "feature_locked_hit",
  UpgradeRequestSubmitted: "upgrade_request_submitted",
  NudgeHrSubmitted: "nudge_hr_submitted",
  OperatorMarkPaid: "operator_mark_paid",
  OperatorExtendTrial: "operator_extend_trial",
  OperatorRevoke: "operator_revoke",
  OperatorBulkMarkPaid: "operator_bulk_mark_paid",
  OperatorBulkExtendTrial: "operator_bulk_extend_trial",
  OperatorBulkRevoke: "operator_bulk_revoke",
  ReferenceCreated: "reference_created",
  ReferenceUpdated: "reference_updated",
  ReferenceDeactivated: "reference_deactivated",
} as const;
